
/**
 * Module dependencies.
 */

import { Fill } from 'react-components/layout';
import { color } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  isClickable?: boolean;
  isVisible: boolean;
  onRequestClose: (event: KeyboardEvent | Event) => void;
};

/**
 * `Backdrop` styled component.
 */

const Backdrop = styled(Fill)<{ isClickable: boolean; isVisible: boolean }>`
  background-color: ${color.transparentize('secondary', 0.6)};
  opacity: 0;
  pointer-events: none;
  position: fixed;
  transition: opacity ${theme('animations.defaultTransition')};
  z-index: ${theme('zIndex.modal.backdrop')};

  ${ifProp('isClickable', css`
    cursor: pointer;
  `)};

  ${ifProp('isVisible', css`
    opacity: 1;
    pointer-events: all;
  `)}
`;

/**
 * Export `ModalBackdrop` component.
 */

export function ModalBackdrop(props: Props): JSX.Element {
  const { isClickable = true, isVisible, onRequestClose, ...rest } = props;

  useEffect(() => {
    function handleEscKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onRequestClose(event);
      }
    }

    if (isClickable && isVisible) {
      document.addEventListener('keydown', handleEscKey, false);

      return () => {
        document.removeEventListener('keydown', handleEscKey, false);
      };
    }
  }, [isClickable, isVisible, onRequestClose]);

  const handleClick = useCallback((event: Event) => {
    if (!isClickable) {
      return;
    }

    onRequestClose(event);
  }, [isClickable, onRequestClose]);

  return (
    <Backdrop
      {...rest}
      isClickable={isClickable}
      isVisible={isVisible}
      onClick={handleClick}
    />
  );
}
